/* eslint-disable import/no-named-as-default */
import React, { Component, Fragment, ReactNode } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Donut from "../../Charts/Donut";
import SpendPerSectorModal from "./SpendPerSectorModal";
import TimeScaleDropDown from "./TimeScaleDropDown";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import {
  Wrapper,
  ShowMore,
  MobileLegend,
} from "../styles/spend-per-sector-styles";
import colorPalette from "../colorPalette/colorPalette";
import { ChartTitle } from "../styles/employer-analytics-styles";

interface ISpendPerSectorProps {
  currencySymbol: string;
  spendPerSector: {
    sectors?: Array<string>;
    values?: Array<number>;
  };
  spendPerSectorWidgetLoader: boolean;
}

interface ISpendPerSectorState {
  spendPerSectorModalActive: boolean;
  timescaleDropdownActive: boolean;
  activeTimescale: {
    id: number;
    name: string;
  };
}

interface IFormatterProps {
  seriesIndex: number;
  w: {
    config: {
      series: () => void;
    };
  };
  globals: {
    seriesTotals: {
      reduce: (a, b) => number;
    };
  };
}

export class SpendPerSector extends Component<
  ISpendPerSectorProps,
  ISpendPerSectorState
> {
  static propTypes = {
    currencySymbol: PropTypes.string.isRequired,
    spendPerSector: PropTypes.shape({
      sectors: PropTypes.arrayOf(PropTypes.string),
      values: PropTypes.arrayOf(PropTypes.number),
    }),
    spendPerSectorWidgetLoader: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    spendPerSector: {
      sectors: [],
      values: [],
    },
  };

  state = {
    spendPerSectorModalActive: false,
    timescaleDropdownActive: false,
    activeTimescale: {
      id: 0,
      name: "All Time",
    },
  };

  toggleModal = (): void => {
    this.setState(({ spendPerSectorModalActive }) => ({
      spendPerSectorModalActive: !spendPerSectorModalActive,
    }));
  };

  toggleDropdown = (): void => {
    this.setState(({ timescaleDropdownActive }) => ({
      timescaleDropdownActive: !timescaleDropdownActive,
    }));
  };

  selectTimescale = (timescale: { id: number; name: string }): void => {
    this.setState({
      activeTimescale: timescale,
    });
  };

  render(): ReactNode {
    const { currencySymbol, spendPerSector, spendPerSectorWidgetLoader } =
      this.props;

    const {
      timescaleDropdownActive,
      activeTimescale,
      spendPerSectorModalActive,
    } = this.state;

    const spendPerSectorOptions = {
      series: spendPerSector.values,
      options: {
        chart: {
          width: 330,
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 4,
            opacity: 0.1,
          },
        },
        colors: colorPalette(spendPerSector.sectors.length),
        labels: spendPerSector.sectors,
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            customScale: 1,
            offsetX: 0,
            offsetY: 0,
            expandOnClick: true,
            dataLabels: {
              offset: 0,
              minAngleToShowLabel: 10,
            },
            donut: {
              size: "65%",
              background: "transparent",
              labels: {
                show: true,
                value: {
                  show: true,
                  fontSize: "25px",
                  fontFamily: "Komet, sans-serif",
                  color: "#333",
                  offsetY: 6,
                  formatter: (val: string) => val,
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Total",
                  fontSize: "18px",
                  fontFamily: "Komet, sans-serif",
                  color: "#333",
                  formatter: (w: IFormatterProps) => {
                    const value = w.globals.seriesTotals.reduce(
                      (a: number, b: number) => a + b,
                      0
                    );
                    return `${currencySymbol}${value}`;
                  },
                },
              },
            },
          },
        },
        tooltip: {
          enabled: true,
          style: {
            fontSize: "14px",
            fontFamily: "Komet, sans-serif",
          },
          x: {
            title: {
              formatter: (seriesName) => seriesName as unknown as string,
            },
          },
          y: {
            formatter: (value: number) => `${currencySymbol}${value}`,
          },
        },
        responsive: [
          {
            breakpoint: 1199,
            options: {
              chart: {
                width: 375,
              },
              legend: {
                show: false,
              },
              dataLabels: {
                style: {
                  fontSize: "12px",
                },
              },
            },
          },
        ],
      },
    };

    const mobileLegendOptions = {
      series: spendPerSector.values,
      options: {
        colors: colorPalette(spendPerSector.sectors.length),
        labels: spendPerSector.sectors,
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: true,
          horizontalAlign: "left",
          formatter: (name: string, { seriesIndex, w }: IFormatterProps) =>
            `${name} - ${currencySymbol}${
              w.config.series[seriesIndex] as number
            }`,
          itemMargin: {
            horizontal: 4,
            vertical: 4,
          },
          position: "bottom",
          containerMargin: {
            top: 0,
          },
        },
        tooltip: {
          enabled: false,
        },
        chart: {
          width: "100%",
          height: 100,
        },
      },
    };

    const timescaleOptions = [
      { id: 0, name: "All Time" },
      { id: 7, name: "Last 7 days" },
      { id: 30, name: "Last 30 days" },
      { id: 90, name: "Last 90 days" },
    ];

    return (
      <Fragment>
        {spendPerSectorModalActive && (
          <SpendPerSectorModal toggleModal={this.toggleModal} />
        )}
        <Wrapper
          className="animated fadeIn delay-5"
          id="spend-per-sector-widget"
        >
          <AnalyticsWidgetLoading
            text="Updating..."
            active={spendPerSectorWidgetLoader}
          />
          <ChartTitle
            marginBottom={20}
            data-test-id="spend-per-sector-chart-title"
          >
            Spend per Sector
            <TimeScaleDropDown
              toggleDropdown={this.toggleDropdown}
              dropdownActive={timescaleDropdownActive}
              selectTimescale={this.selectTimescale}
              activeTimescale={activeTimescale}
              options={timescaleOptions}
              type="SPEND_PER_SECTOR"
              testId="spend-per-sector-chart-timescale-dropdown"
            />
          </ChartTitle>
          <Donut
            graphOptions={spendPerSectorOptions}
            dataTestId="spend-per-sector-chart"
          />
          {spendPerSector.values.length > 0 && (
            <ShowMore
              data-test-id="spend-per-sector-show-more"
              onClick={this.toggleModal}
            >
              show more
            </ShowMore>
          )}
          <MobileLegend>
            <Donut graphOptions={mobileLegendOptions} placeholderHidden />
          </MobileLegend>
        </Wrapper>
      </Fragment>
    );
  }
}

interface IReduxState {
  employerAnalytics: {
    currencySymbol?: string;
    chartsData: {
      spendPerSector: {
        sectors?: Array<string>;
        values?: Array<number>;
      };
    };
    spendPerSectorWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  currencySymbol: state.employerAnalytics.currencySymbol,
  spendPerSector: state.employerAnalytics.chartsData.spendPerSector,
  spendPerSectorWidgetLoader:
    state.employerAnalytics.spendPerSectorWidgetLoader,
});

export default connect(mapStateToProps, {})(SpendPerSector);
